import { Trophy, Clock, Target } from 'lucide-react'
import type { ScoringRules } from '@/hooks/useLeagueConfig'

interface FirstPickBannerProps {
  matchdayNumber: number
  countdown: {
    days: number
    hours: number
    minutes: number
    seconds: number
    isExpired: boolean
    hasDeadline: boolean
  }
  scoringRules: ScoringRules
}

export function FirstPickBanner({ matchdayNumber, countdown, scoringRules }: FirstPickBannerProps) {
  const timeDisplay = countdown.hasDeadline && !countdown.isExpired
    ? countdown.days > 0
      ? `${countdown.days}d ${countdown.hours}h`
      : countdown.hours > 0
        ? `${countdown.hours}h ${countdown.minutes}m`
        : `${countdown.minutes}m ${countdown.seconds}s`
    : null

  return (
    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30
                    border border-emerald-200 dark:border-emerald-800/50 rounded-xl p-5 mb-6">
      <div className="flex items-start gap-4">
        <div className="bg-emerald-100 dark:bg-emerald-900/50 p-2.5 rounded-lg shrink-0">
          <Target className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-base font-bold text-zinc-800 dark:text-zinc-100">
              This is your first pick!
            </h3>
            <span className="text-xs font-medium bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 rounded-full">
              Matchday {matchdayNumber}
            </span>
          </div>

          <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed mb-3">
            {matchdayNumber > 1
              ? `Your league is starting at Matchday ${matchdayNumber}. Pick a team you think will win — you can change your pick until the deadline.`
              : 'Choose a team you think will win this matchday. You can change your pick until the deadline.'}
          </p>

          <div className="flex flex-wrap items-center gap-4 text-xs text-zinc-500 dark:text-zinc-400">
            {timeDisplay && (
              <div className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                <span>Deadline in <span className="font-semibold text-zinc-700 dark:text-zinc-300">{timeDisplay}</span></span>
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <Trophy className="w-3.5 h-3.5" />
              <span>Win <span className="font-semibold text-emerald-600 dark:text-emerald-400">+{scoringRules.win}</span>, Draw <span className="font-semibold text-amber-600 dark:text-amber-400">+{scoringRules.draw}</span>, Loss <span className="text-zinc-400">{scoringRules.loss}</span></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
